import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CommonPropertyProps, Payload, SortValue } from "schemas/elements";
import {
	Button,
	Elem,
	ElemBefore,
	IconButton,
	SearchSelect,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import { orderOptions } from "../options";
import { useTranslation } from "react-i18next";
import { useCreateOptions } from "hooks/FormDesigner/useCreateOptions";
import { orderValues } from "../../../../constants";
import classnames from "classnames";

type Props = Omit<CommonPropertyProps, "value"> & {
	value: Payload;
	nextProp?: string;
	options: SearchSelectOptionProps[];
	disabledPlaceholder: string;
};

export default ({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	nextProp,
	id,
	disabledPlaceholder,
	options,
	nestedField,
	isEmu,
}: Props) => {
	const { t } = useTranslation();

	const newItem = useMemo(() => {
		return { field: undefined, order: orderValues.ASC };
	}, []);

	const [init, setInit] = useState(false);

	// Create order options with translations
	const orderOps = useCreateOptions(orderOptions);

	/**
	 * Format Sort values to form designer format
	 *
	 * @param values sort values from store
	 */
	const handleFormatValues = useCallback(values => {
		return values.map((val: SortValue) => {
			return {
				field: val.field
					? {
							value: val.field,
							label: `${val.field?.replace(/\//, "")}`,
					  }
					: undefined,
				order: val.order,
			};
		});
	}, []);

	const [values, setValues] = useState(
		value.sort.length ? handleFormatValues(value.sort) : []
	);

	const [requestUri, setRequestUri] = useState(
		value && value["request-uri"] ? value["request-uri"] : undefined
	);

	/**
	 * Reformat Sort values to form service format
	 *
	 * @param values sort values in form designer
	 */
	const handleReformatValues = useCallback(values => {
		return values.map(
			({
				field,
				order,
			}: {
				field: SearchSelectOptionProps;
				order: string;
			}) => {
				const val = field ? field.value : undefined;
				return { field: val, order };
			}
		);
	}, []);

	const handleUpdateStore = useCallback(
		newVals => {
			const vals = newVals?.length ? handleReformatValues(newVals) : [];
			updateStore({
				elementIndex,
				property: field,
				nestedField,
				value: nextProp
					? {
							...value,
							[nextProp]: vals,
					  }
					: vals,
			});
		},
		[
			elementIndex,
			field,
			handleReformatValues,
			nestedField,
			nextProp,
			updateStore,
			value,
		]
	);

	const handleChange = useCallback(
		(newVal, index, property) => {
			const val = property === "order" ? newVal.value : newVal;
			const currentValues = [...values];
			currentValues[index] = {
				...currentValues[index],
				[property]: val,
			};
			setValues(currentValues);
			handleUpdateStore(currentValues);
		},
		[handleUpdateStore, values]
	);

	const handleAdd = useCallback(() => {
		setValues([...values, newItem]);
	}, [newItem, values]);

	const handleRemove = useCallback(
		index => {
			const currentValues = [...values];
			currentValues.splice(index, 1);
			setValues(currentValues);
		},
		[values]
	);

	useEffect(() => {
		if (init) handleUpdateStore(values);
		else setInit(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.length]);

	useEffect(() => {
		if (field === "payload" && value) {
			if (!value["request-uri"] || requestUri !== value["request-uri"]) {
				setValues([]);
				setRequestUri(value["request-uri"]);
			}
		}
	}, [field, requestUri, value]);

	return (
		<Elem className="elem-twin-input">
			<ElemBefore
				label={
					nextProp
						? t(`formDesigner.properties.${nextProp}`)
						: t(`formDesigner.properties.${field}`)
				}
				required={required}
				id={id}
			/>
			<div className="hint-text">
				{t(`formDesigner.elements.${desc}`)}
			</div>
			<div className={`elem-twin-input-properties`}>
				{values.map(
					(
						{
							field,
							order,
						}: { field: SearchSelectOptionProps; order: string },
						index: number
					) => {
						return (
							<div className="elem-twin-input-item" key={index}>
								<SearchSelect
									id={`${id}-${index}-field`}
									onChange={val =>
										handleChange(val, index, "field")
									}
									key={`${id}-${index}-field`}
									label={t("formDesigner.labels.field")}
									labelHidden={true}
									value={field && options ? field : undefined}
									placeholder={t("formDesigner.labels.field")}
									options={options}
									disabled={!options}
									className={classnames(
										validation && !field ? " invalid" : "",
										!isEmu ? "text-monospace" : ""
									)}
									clearable={true}
								/>
								<SearchSelect
									id={`${id}-${index}-order`}
									onChange={val =>
										handleChange(val, index, "order")
									}
									key={`${id}-${index}-order`}
									label={t("formDesigner.labels.order")}
									labelHidden={true}
									value={
										order
											? {
													value: order,
													label: t(
														`lists.order.${order}`
													),
											  }
											: undefined
									}
									placeholder={t("formDesigner.labels.order")}
									options={orderOps}
									className={
										validation && !order ? " invalid" : ""
									}
								/>
								<IconButton
									label={t("formDesigner.labels.remove")}
									icon="times"
									onClick={() => handleRemove(index)}
									className="btn-outline-danger"
								/>
							</div>
						);
					}
				)}
				{!options && <div className="mb-3">{disabledPlaceholder}</div>}
				<Button
					label={t("formDesigner.labels.add")}
					onClick={handleAdd}
					className="btn-default"
					disabled={!requestUri}
				/>
			</div>
		</Elem>
	);
};
